/*------------------------------------------------------------------------------
   About      : Generic permission hook (RBAC v2)

   One `can(requirement)` covers single / anyOf / allOf / group checks — no
   per-feature canX() methods. Data comes from rc.accessInfo, populated during
   session init (RouteGuard blocks rendering until then), so grants are always
   loaded before gated components mount. Deny-by-default when unset.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useMemo }               from 'react'
import { useGlobalConfig }       from 'context/GlobalContext'
import { checkAccess,
         isDevPermissionBypass,
         PermissionRequirement } from 'framework/access/accessControl'
import { uiPageRequirement }      from 'framework/access/UiRegistry'
import type { RouteEntry }       from 'routes/routeTypes'

export interface PermissionsApi {
  can        : (req : PermissionRequirement) => boolean
  role       : string | undefined
  isBypassed : boolean
}

export const usePermissions = () : PermissionsApi => {

  const { rc } = useGlobalConfig()

  return useMemo(() => {

    // One merged set: proxypulse BFF feature endpoints + granted ui.* ids
    // arrive together from the permission-tree fetch. Strapi action ids from
    // login are merged too but nothing references them anymore (Strapi-direct
    // modules use legacy role if/else, not this catalog-driven layer).
    const granted    = new Set([
      ...rc.accessInfo.getGrantedActions(),
      ...rc.accessInfo.getGrantedBffFeatures(),
    ])
    const role       = rc.accessInfo.getRoleName()
    const isBypassed = isDevPermissionBypass()
      || rc.accessInfo.getIsSuperAdmin()

    return {
      can : (req : PermissionRequirement) => isBypassed || checkAccess(granted, req),
      role,
      isBypassed,
    }
  }, [rc])
}

/**
 * Returns a predicate for ROUTE_TREE entries — true when the entry has no
 * permission requirement or the user satisfies it. Use to filter nav/drawer
 * items from the same source of truth as route guarding.
 *
 * AND-composed with the entry's `uiPageId` (if registered + enforced): a
 * ui.page.* grant never substitutes for the entry's own `permissions` — it
 * can only additionally hide an otherwise-permitted page.
 */
export const useCanAccessRoute = () => {

  const { can } = usePermissions()
  return (entry : RouteEntry) => {
    if (entry.permissions && !can(entry.permissions)) return false
    const uiRequirement = uiPageRequirement(entry.uiPageId)
    if (uiRequirement && !can(uiRequirement)) return false
    return true
  }
}
